import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ArrowLeft, LoaderCircle, Save } from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";
import SeoFields from "../seo/SeoFields";

const EMPTY_FORM = {
  name: "",
  subtitle: "",
  image: null,
  description: "",
  category_id: "",
  display_order: "0",
  short_description: "",
  role_tag: "",
  featured_title: "",
  featured_description: "",
  visionary_leadership_title: "",
  visionary_leadership_description: "",
  proven_expertise_title: "",
  proven_expertise_description: "",
  verification_label: "",
  verification_value: "",
  affiliation_label: "",
  affiliation_value: "",
  meta_title: "",
  meta_description: "",
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

const isMeaningfulHtml = (html) =>
  html && html.replace(/<(.|\n)*?>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;

const TeamFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const isEditMode = mode === "edit";
  const pageTitle = useMemo(() => (isEditMode ? "Edit Team Member" : "Create Team Member"), [isEditMode]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiClient.get("/team-categories");
        setCategories(response.data?.data?.categories || []);
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load team categories");
      }
    };

    fetchCategories();
  }, [showErrorToast]);

  useEffect(() => {
    if (!isEditMode) {
      setIsPageLoading(false);
      return;
    }

    const fetchMember = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/team/${id}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast("Team member not found");
          navigate("/team");
          return;
        }
        setForm({
          name: item.name || "",
          subtitle: item.subtitle || "",
          image: null,
          description: item.description || "",
          category_id: item.category_id || "",
          display_order: String(item.display_order ?? 0),
          short_description: item.short_description || "",
          role_tag: item.role_tag || "",
          featured_title: item.featured_title || "",
          featured_description: item.featured_description || "",
          visionary_leadership_title: item.visionary_leadership_title || "",
          visionary_leadership_description: item.visionary_leadership_description || "",
          proven_expertise_title: item.proven_expertise_title || "",
          proven_expertise_description: item.proven_expertise_description || "",
          verification_label: item.verification_label || "",
          verification_value: item.verification_value || "",
          affiliation_label: item.affiliation_label || "",
          affiliation_value: item.affiliation_value || "",
          meta_title: item.meta_title || "",
          meta_description: item.meta_description || "",
        });
        setImagePreviewUrl(item.image_url || "");
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load team member");
        navigate("/team");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchMember();
  }, [id, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required.";
    if (!form.subtitle.trim()) nextErrors.subtitle = "Sub title is required.";
    if (!isEditMode && !form.image) nextErrors.image = "Image is required.";
    if (!isMeaningfulHtml(form.description)) nextErrors.description = "Description is required.";
    if (!form.category_id) nextErrors.category_id = "Team category is required.";
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("subtitle", form.subtitle.trim());
      payload.append("description", form.description);
      payload.append("category_id", form.category_id);
      payload.append("display_order", form.display_order || "0");
      payload.append("short_description", form.short_description.trim());
      payload.append("role_tag", form.role_tag.trim());
      payload.append("featured_title", form.featured_title.trim());
      payload.append("featured_description", form.featured_description);
      payload.append("visionary_leadership_title", form.visionary_leadership_title.trim());
      payload.append("visionary_leadership_description", form.visionary_leadership_description);
      payload.append("proven_expertise_title", form.proven_expertise_title.trim());
      payload.append("proven_expertise_description", form.proven_expertise_description);
      payload.append("verification_label", form.verification_label.trim());
      payload.append("verification_value", form.verification_value.trim());
      payload.append("affiliation_label", form.affiliation_label.trim());
      payload.append("affiliation_value", form.affiliation_value.trim());
      payload.append("meta_title", form.meta_title.trim());
      payload.append("meta_description", form.meta_description.trim());
      if (form.image) payload.append("image", form.image);

      if (isEditMode) {
        await apiClient.put(`/team/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Team member updated successfully");
      } else {
        await apiClient.post("/team", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Team member created successfully");
      }

      navigate("/team");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save team member");
    } finally {
      setIsSaving(false);
    }
  };

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading team member editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate("/team")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Team
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSave} className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Name</label>
              <Input value={form.name} onChange={(event) => handleInputChange("name", event.target.value)} className={`rounded-lg ${formErrors.name ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.name}</FieldError>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Sub Title</label>
              <Input value={form.subtitle} onChange={(event) => handleInputChange("subtitle", event.target.value)} className={`rounded-lg ${formErrors.subtitle ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.subtitle}</FieldError>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Role Tag</label>
                <Input value={form.role_tag} onChange={(event) => handleInputChange("role_tag", event.target.value)} className="rounded-lg" placeholder="Chairman" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Featured Title</label>
                <Input value={form.featured_title} onChange={(event) => handleInputChange("featured_title", event.target.value)} className="rounded-lg" placeholder="Executive Chairman & Trustee" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Team Category</label>
              <Select value={form.category_id} onValueChange={(value) => handleInputChange("category_id", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError>{formErrors.category_id}</FieldError>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Display Order</label>
              <Input
                type="number"
                min="0"
                value={form.display_order}
                onChange={(event) => handleInputChange("display_order", event.target.value)}
                className="rounded-lg"
              />
              <p className="text-xs text-slate-500">Lower numbers appear first within the selected category.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Short Description</label>
              <textarea value={form.short_description} onChange={(event) => handleInputChange("short_description", event.target.value)} rows={4} className="min-h-[110px] w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-primary/30" placeholder="Short excerpt for card/listing view..." />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Professional Profile</label>
              <div className={`overflow-hidden rounded-lg border bg-white ${formErrors.description ? "border-red-300" : "border-slate-200"}`}>
                <ReactQuill theme="snow" value={form.description} onChange={(value) => handleInputChange("description", value)} modules={quillModules} placeholder="Write description here..." />
              </div>
              <FieldError>{formErrors.description}</FieldError>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Featured Description</label>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <ReactQuill theme="snow" value={form.featured_description} onChange={(value) => handleInputChange("featured_description", value)} modules={quillModules} placeholder="Write featured section content here..." />
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Visionary Leadership Title</label>
                  <Input value={form.visionary_leadership_title} onChange={(event) => handleInputChange("visionary_leadership_title", event.target.value)} className="rounded-lg" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Visionary Leadership Description</label>
                  <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    <ReactQuill theme="snow" value={form.visionary_leadership_description} onChange={(value) => handleInputChange("visionary_leadership_description", value)} modules={quillModules} placeholder="Write visionary leadership content here..." />
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Proven Expertise Title</label>
                  <Input value={form.proven_expertise_title} onChange={(event) => handleInputChange("proven_expertise_title", event.target.value)} className="rounded-lg" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Proven Expertise Description</label>
                  <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    <ReactQuill theme="snow" value={form.proven_expertise_description} onChange={(value) => handleInputChange("proven_expertise_description", value)} modules={quillModules} placeholder="Write proven expertise content here..." />
                  </div>
                </div>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Verification Label</label>
                    <Input value={form.verification_label} onChange={(event) => handleInputChange("verification_label", event.target.value)} className="rounded-lg" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Verification Value</label>
                    <Input value={form.verification_value} onChange={(event) => handleInputChange("verification_value", event.target.value)} className="rounded-lg" />
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Affiliation Label</label>
                    <Input value={form.affiliation_label} onChange={(event) => handleInputChange("affiliation_label", event.target.value)} className="rounded-lg" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Affiliation Value</label>
                    <Input value={form.affiliation_value} onChange={(event) => handleInputChange("affiliation_value", event.target.value)} className="rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <InlineFormFileUpload label="Image" value={form.image} onChange={(file) => handleInputChange("image", file)} existingFiles={imagePreviewUrl ? [imagePreviewUrl] : []} accept={IMAGE_TYPES} maxSizeBytes={10 * 1024 * 1024} required={!isEditMode} error={formErrors.image} helperText="JPG, PNG, GIF or WEBP — maximum 10 MB" />
              <ImageUploadGuidance requirementKey="teamMember" file={form.image} src={imagePreviewUrl} />
            </div>
          </div>
        </div>

        <SeoFields
          metaTitle={form.meta_title}
          metaDescription={form.meta_description}
          onChange={handleInputChange}
          className="mt-6"
        />

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate("/team")}>Cancel</Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isSaving ? "Saving..." : isEditMode ? "Update Team Member" : "Create Team Member"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TeamFormPage;
